import BubbleCommon, {
  summaryMentionsRatioTips,
  summaryNegativeRatioTips,
  summaryPositiveRatioTips,
} from '@op/components/BubbleCommon';
import HoverSortCommon from '@op/components/HoverSortCommon';
import PercentRatioOrChart from '@op/components/TableCommonHook/columns/components/PercentRatioOrChart';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { dealwithTopicList } from '@op/pages/Summary/utils';
import { getTierTopicList } from '@op/services';
import { arrayToSimpleObj } from '@op/utils';
import { decimalFormatPercentage, sentimentFormat } from '@op/utils/value';
import React, { useEffect, useState } from 'react';
import { getLocale, useIntl } from 'umi';
import { SentimentTypeFilterMap } from '../config';

export const COLUMN_CONFIG = [
  {
    key: 'mentions',
    header: 'Mentions',
    valueKey: 'mentions',
    hasCharts: true,
    changeSort: true,
  },
  {
    key: 'mentions_ratio',
    header: 'Mentions%',
    valueKey: 'mentions_ratio',
    formatValue: decimalFormatPercentage,
    hasCharts: true,
    sortItem: 'mentions',
    changeSort: true,
    tips: summaryMentionsRatioTips,
  },
  {
    key: 'avg_sentiment',
    header: 'Sentiment',
    valueKey: 'avg_sentiment',
    formatValue: sentimentFormat,
    changeSort: true,
  },
  {
    key: 'negative_mentions_ratio',
    header: 'Negative%-2',
    valueKey: 'negative_mentions_ratio',
    formatValue: decimalFormatPercentage,
    changeSort: true,
    sortItem: 'negative_mentions',
    tips: summaryNegativeRatioTips,
  },
  {
    key: 'positive_mentions_ratio',
    header: 'Positive%-2',
    valueKey: 'positive_mentions_ratio',
    formatValue: decimalFormatPercentage,
    sortItem: 'positive_mentions',
    changeSort: true,
    tips: summaryPositiveRatioTips,
  },
];

export const topicTrackingSortItemMap = arrayToSimpleObj(
  COLUMN_CONFIG,
  'sortItem',
  'valueKey',
);

export default function useTopicTrackingTableColumns({
  timelineMap,
  filter,
  transTopic,
  sortInfo,
  setPageInfo,
  topicLevel,
}: any) {
  const { formatMessage } = useIntl();
  const [sort, setSort] =
    sortInfo ||
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useState<{ by: string; order: 'desc' | 'asc'; }>({
      by: 'mentions',
      order: 'desc',
    });

  const [columns, setColumns] = useState<Array<any>>([]);
  const [sortList, setSortList] = useState<Array<string>>([]);

  useEffect(() => {
    const sortList: Array<string> = [];
    const columns: Array<any> = COLUMN_CONFIG.map(
      ({ key, header, valueKey, formatValue, hasCharts, changeSort, tips }) => {
        let headerText: any = formatMessage({ id: header });

        if (changeSort) {
          headerText = (
            <HoverSortCommon
              sort={sort}
              sortOptions={[
                {
                  text: headerText,
                  value: valueKey,
                },
                {
                  text: formatMessage({ id: `${header}: Change%` }),
                  value: `${valueKey  }_qoq`,
                },
              ]}
              sortChange={(sort: { by: string; order: 'asc' | 'desc'; }) => {
                if (sort.by.includes('qoq') && setPageInfo) {
                  setPageInfo({ page: 1, page_size: 1000 });
                }
                buriedBtnPoint('STO_ListRank', sort.by);
                setSort(sort);
              }}
            >
              <BubbleCommon tipsMap={tips} showIcon={false} placement="top">
                <span>{headerText}</span>
              </BubbleCommon>
            </HoverSortCommon>
          );
        } else {
          sortList.push(valueKey);
        }

        return {
          key,
          header: headerText,
          align: 'right',
          width: hasCharts ? 260 : 160,
          render: (record: any) => {
            const topic = record.topic.toLocaleLowerCase();
            return (
              <PercentRatioOrChart
                filter={filter}
                data={record}
                valueKey={valueKey}
                header={formatMessage({ id: header })}
                formatValue={formatValue}
                timeline={timelineMap[valueKey]?.[topic]}
                name={transTopic(topic, topicLevel, getLocale())}
              ></PercentRatioOrChart>
            );
          },
        };
      },
    );

    setColumns(columns);
    setSortList(sortList);
  }, [timelineMap, sort, filter, formatMessage]);

  return {
    columns,
    sortList,
    sort,
    setSort,
  };
}

export function useTopicLevelOneList({ filter, sentimentType }: any) {
  const [total, setTotal] = useState(0);
  const [tableData, setTableData] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(false);

  const requestServerData = () => {
    setLoading(true);
    setTableData([]);
    getTierTopicList({
      ...filter,
      topic_level: 'L1',
      metrics: ['mentions', 'sentiment'],
      types: ['total', 'link'],
      page: 1,
      page_size: 3,
      sort_item: 'mentions',
      sort_model: 'desc',
      topic_rule: "include",
      sentiment_type: 'total',
      sentiment: [SentimentTypeFilterMap[sentimentType]],
      social_media_by_account: filter?.social_media_by_account || undefined,
      social_media_by_keyword: undefined,
      topicType: 'all_topic'
    }).then((data: { list: any[]; total: number; }) => {
      const { tableData, total } = dealwithTopicList(data, filter);
      setTableData(tableData);
      setTotal(total);
      setLoading(false);
    });
  };

  // 拿快捷选项
  useEffect(() => {
    if (!filter?.date_type) return;
    if (filter.edition_unified_id) {
      requestServerData();
    }
  }, [filter]);

  return { total, tableData, loading };
}
